'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

interface Indicator {
  id: number;
  title: string;
  description: string;
  icon: string;
}

const TrustIndicators = () => {
  const { t } = useTranslation();
  const indicators: Indicator[] = [
    {
      id: 1,
      title: t('verifiedManufacturers'),
      description: t('verifiedManufacturersDesc'),
      icon: 'ShieldCheckIcon',
    },
    {
      id: 2,
      title: t('securePlatform'),
      description: t('securePlatformDesc'),
      icon: 'LockClosedIcon',
    },
    {
      id: 3,
      title: t('qualityAssurance'),
      description: t('qualityAssuranceDesc'),
      icon: 'CheckBadgeIcon',
    },
    {
      id: 4,
      title: t('globalStandards'),
      description: t('globalStandardsDesc'),
      icon: 'GlobeAltIcon',
    },
  ];

  const certifications = [
    { name: t('iso9001'), icon: 'DocumentCheckIcon' },
    { name: t('gdprCompliant'), icon: 'ShieldCheckIcon' },
    { name: t('platformSslSecured'), icon: 'LockClosedIcon' },
    { name: t('platformSoc2'), icon: 'CheckBadgeIcon' },
  ];

  return (
    <section className="py-20 px-6 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            {t('trustSecurity')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('trustSecurityDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {indicators.map((indicator) => (
            <div
              key={indicator.id}
              className="bg-background border border-border rounded-lg p-6 text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon
                    name={indicator.icon as any}
                    size={28}
                    variant="solid"
                    className="text-success"
                  />
                </div>
              </div>
              <h3 className="text-lg font-headline font-bold text-foreground mb-3">
                {indicator.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {indicator.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-background border border-border rounded-lg p-8">
          <h3 className="text-xl font-headline font-bold text-foreground text-center mb-8">
            {t('platformCertifications')}
          </h3>
          <div className="flex flex-wrap justify-center gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-6 py-3 bg-muted rounded-lg border border-border"
              >
                <Icon
                  name={cert.icon as any}
                  size={24}
                  variant="solid"
                  className="text-primary"
                />
                <span className="text-sm font-semibold text-foreground">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;